import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  acceptanceTerms,
  brokers,
  termAcceptances,
  type TermsAcceptance,
  type TermsApproval,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';
import { classeDaAba, trilhoDeAbas } from '@/shared/ui/tabStyles';

import { PartyToolbar } from '../shared/PartyToolbar';
import {
  PERMISSAO_VER_TERMO,
  ROTULO_DO_TIPO,
  TIPOS_DE_TERMO_DE_ACEITE,
} from './rotulos';
import { TermsSettingsPanel } from './TermsSettingsPanel';

type Aba = 'aceites' | 'declaracoes' | 'versoes';

const abas: { chave: Aba; rotulo: string }[] = [
  { chave: 'aceites', rotulo: 'Termos de Aceite' },
  { chave: 'declaracoes', rotulo: 'Declaracoes da proposta' },
  // As versoes e a configuracao, as mesmas da aba Termos das configuracoes. Ficam
  // tambem aqui para quem cuida do texto sem ler as configuracoes da empresa (o
  // juridico, por padrao).
  { chave: 'versoes', rotulo: 'Versoes dos termos' },
];

const limite = 20;

/**
 * Tela /termos-aceite (TER-001 a TER-007).
 *
 * A aba "Termos de Aceite" e a lista do OnPoint: Corretor, Data e hora da criacao e
 * o Termo de aceite (o comprovante para baixar), com a pessoa e a versao que o
 * SeguraCore acrescenta. A corretora ve so os aceites dela; a seguradora ve todos,
 * com o filtro Corretora e o aviso "Atencao!" que o OnPoint mostra enquanto a
 * corretora nao foi escolhida. A aba "Declaracoes da proposta" lista quem declarou o
 * termo de aceite na emissao, no endosso ou na cotacao. A aba "Versoes dos termos"
 * aparece para quem le as versoes (termos.termo.view), e nunca para a corretora.
 */
export function TermsAcceptancesPage() {
  const { session, can } = useAuth();
  const daCorretora = session?.broker_scoped === true;
  const [aba, setAba] = useState<Aba>('aceites');
  const abasVisiveis = abas.filter(
    (item) => item.chave !== 'versoes' || (!daCorretora && can(PERMISSAO_VER_TERMO)),
  );

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Termos de aceite"
        description={
          daCorretora
            ? 'Os aceites do termo de uso e as declaracoes das pessoas da sua corretora.'
            : 'Quem aceitou o termo de uso, quando, de onde e qual versao, e as declaracoes da proposta.'
        }
      />

      <div role="tablist" aria-label="Termos de aceite" className={trilhoDeAbas}>
        {abasVisiveis.map((item) => (
          <button
            key={item.chave}
            type="button"
            role="tab"
            id={`aba-${item.chave}`}
            aria-selected={aba === item.chave}
            aria-controls={`painel-${item.chave}`}
            onClick={() => setAba(item.chave)}
            className={aba === item.chave ? classeDaAba(true) : classeDaAba(false)}
          >
            {item.rotulo}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`painel-${aba}`} aria-labelledby={`aba-${aba}`}>
        {aba === 'aceites' ? <ListaDeAceites daCorretora={daCorretora} /> : null}
        {aba === 'declaracoes' ? (
          <ListaDeDeclaracoes daCorretora={daCorretora} />
        ) : null}
        {aba === 'versoes' ? <TermsSettingsPanel /> : null}
      </div>
    </div>
  );
}

/** Corretoras para o filtro da seguradora. A corretora nao escolhe: o servidor fixa. */
function useCorretoras(daCorretora: boolean) {
  const { can } = useAuth();
  return useQuery({
    queryKey: ['brokers', 'filtro-termos'],
    queryFn: ({ signal }) => brokers.list({ limit: 100 }, signal),
    enabled: !daCorretora && can('corretores.corretor.view'),
  });
}

function opcoesDeCorretora(itens: { id: string; legal_name: string }[]) {
  return [
    { value: '', label: 'Todas as corretoras' },
    ...itens.map((corretora) => ({ value: corretora.id, label: corretora.legal_name })),
  ];
}

function ListaDeAceites({ daCorretora }: { daCorretora: boolean }) {
  const toast = useToast();
  const [busca, setBusca] = useState('');
  const [corretora, setCorretora] = useState('');
  const [offset, setOffset] = useState(0);
  const [baixando, setBaixando] = useState<string | null>(null);

  const corretoras = useCorretoras(daCorretora);
  const consulta = useQuery({
    queryKey: ['term-acceptances', { busca, corretora, offset }],
    queryFn: ({ signal }) =>
      termAcceptances.list(
        { q: busca, broker_id: corretora, limit: limite, offset, sort: '-accepted_at' },
        signal,
      ),
  });

  async function baixar(aceite: TermsAcceptance) {
    setBaixando(aceite.id);
    try {
      const arquivo = await termAcceptances.download(aceite.id);
      const extensao = arquivo.type.includes('pdf') ? 'pdf' : 'docx';
      const url = URL.createObjectURL(arquivo);
      const link = document.createElement('a');
      link.href = url;
      link.download = `aceite-termo-de-uso-v${aceite.version}.${extensao}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (erro) {
      toast.error(
        erro instanceof Error ? erro.message : 'Nao foi possivel baixar o termo.',
      );
    } finally {
      setBaixando(null);
    }
  }

  const colunas: Column<TermsAcceptance>[] = [
    {
      key: 'corretor',
      header: 'Corretor',
      render: (aceite) => aceite.broker?.label ?? 'Seguradora',
    },
    {
      key: 'pessoa',
      header: 'Pessoa',
      render: (aceite) => (
        <div className="flex flex-col">
          <span className="text-ink-900">{aceite.user_label}</span>
          <span className="text-xs text-ink-500">{aceite.user_email}</span>
        </div>
      ),
    },
    {
      key: 'versao',
      header: 'Versao',
      hideBelow: 'md',
      render: (aceite) => `Versao ${aceite.version}`,
    },
    {
      key: 'data',
      header: 'Data e hora da criacao',
      render: (aceite) => formatDateTime(aceite.accepted_at),
    },
    {
      key: 'termo',
      header: 'Termo de aceite',
      align: 'right',
      render: (aceite) => (
        <AcaoComIcone
          acao="baixar"
          rotulo="Baixar"
          loading={baixando === aceite.id}
          aria-label={`Baixar o termo de aceite de ${aceite.user_label}`}
          onClick={() => void baixar(aceite)}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {!daCorretora && corretora === '' ? (
        <Alert tone="warning" title="Atencao!">
          Escolha a corretora no filtro para ver so os aceites dela. Sem a escolha, a
          lista traz os aceites de todas as corretoras e das pessoas da seguradora.
        </Alert>
      ) : null}
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os aceites">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por pessoa, e-mail ou corretora"
          filter={
            !daCorretora ? (
              <Select
                label="Corretora"
                className="h-9"
                options={opcoesDeCorretora(corretoras.data?.items ?? [])}
                value={corretora}
                onChange={(evento) => {
                  setCorretora(evento.target.value);
                  setOffset(0);
                }}
              />
            ) : null
          }
        />
        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(aceite) => aceite.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="file"
              title="Nenhum aceite registrado"
              description="O aceite aparece aqui quando alguem aceita o termo de uso vigente."
            />
          }
        />
        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>
    </div>
  );
}

function ListaDeDeclaracoes({ daCorretora }: { daCorretora: boolean }) {
  const [tipo, setTipo] = useState('');
  const [corretora, setCorretora] = useState('');
  const [offset, setOffset] = useState(0);

  const corretoras = useCorretoras(daCorretora);
  const consulta = useQuery({
    queryKey: ['acceptance-term-approvals', { tipo, corretora, offset }],
    queryFn: ({ signal }) =>
      acceptanceTerms.approvals(
        {
          kind: tipo,
          broker_id: corretora,
          limit: limite,
          offset,
          sort: '-accepted_at',
        },
        signal,
      ),
  });

  const colunas: Column<TermsApproval>[] = [
    {
      key: 'tipo',
      header: 'Termo',
      render: (declaracao) => ROTULO_DO_TIPO[declaracao.kind],
    },
    {
      key: 'corretor',
      header: 'Corretor',
      render: (declaracao) => declaracao.broker?.label ?? 'Sem corretora',
    },
    {
      key: 'pessoa',
      header: 'Pessoa',
      render: (declaracao) => (
        <div className="flex flex-col">
          <span className="text-ink-900">{declaracao.user_label}</span>
          <span className="text-xs text-ink-500">{declaracao.user_email}</span>
        </div>
      ),
    },
    {
      key: 'lugar',
      header: 'Onde',
      hideBelow: 'md',
      render: (declaracao) =>
        declaracao.operation_id
          ? `Operacao ${declaracao.operation_id.slice(0, 8)}`
          : `Endosso ${declaracao.endorsement_id?.slice(0, 8) ?? ''}`,
    },
    {
      key: 'versao',
      header: 'Versao',
      hideBelow: 'md',
      render: (declaracao) => `Versao ${declaracao.version}`,
    },
    {
      key: 'data',
      header: 'Data e hora da declaracao',
      render: (declaracao) => formatDateTime(declaracao.accepted_at),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as declaracoes">
          {consulta.error.message}
        </Alert>
      ) : null}
      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-5 py-4">
          <Select
            label="Tipo de termo"
            className="h-9"
            options={[
              { value: '', label: 'Todos os tipos' },
              ...TIPOS_DE_TERMO_DE_ACEITE.map((item) => ({
                value: item,
                label: ROTULO_DO_TIPO[item],
              })),
            ]}
            value={tipo}
            onChange={(evento) => {
              setTipo(evento.target.value);
              setOffset(0);
            }}
          />
          {!daCorretora ? (
            <Select
              label="Corretora"
              className="h-9"
              options={opcoesDeCorretora(corretoras.data?.items ?? [])}
              value={corretora}
              onChange={(evento) => {
                setCorretora(evento.target.value);
                setOffset(0);
              }}
            />
          ) : null}
        </div>
        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(declaracao) => declaracao.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="file"
              title="Nenhuma declaracao registrada"
              description="A declaracao aparece aqui quando alguem marca o termo de aceite na emissao, no endosso ou na cotacao."
            />
          }
        />
        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>
    </div>
  );
}
